import { Avatar, Box, Fade, Typography } from "@mui/material";
import React from "react";
import LucideIcon from "../../../../components/LucideIcon";
import { AgentResult } from "../types";
import StepNavButtons from "./StepNavButtons";

interface AgentsInActionStepProps {
  agents?: AgentResult[];
  employeesLower: string;
  onNext: () => void;
  onBack: () => void;
}

export default function AgentsInActionStep({
  agents,
  employeesLower,
  onNext,
  onBack,
}: AgentsInActionStepProps) {
  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
          AI Agents
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          Your AI team is ready
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          Based on your business and your ideal customers, we picked these AI{" "}
          {employeesLower} to put your strategy into action.
        </Typography>

        {!agents?.length ? (
          <Typography color="text.secondary">
            We couldn't generate suggestions this time. You can add {employeesLower} anytime
            after onboarding.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {agents.map((agent, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                }}
              >
                {agent.picture ? (
                  <Avatar src={agent.picture} sx={{ width: 48, height: 48 }} />
                ) : (
                  <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                    <LucideIcon name="Bot" size={24} />
                  </Avatar>
                )}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {agent.name}
                  </Typography>
                  {agent.title && (
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                      {agent.title}
                    </Typography>
                  )}
                  {agent.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {agent.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <StepNavButtons onBack={onBack} onNext={onNext} />
      </Box>
    </Fade>
  );
}
